import _ from 'lodash'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import CONSTANTS from 'core/data-model/constants/values'
import { UPDATE_BIRTHDATE } from 'quick-quote/insured-information/actions'
import {
  editSumAssured,
  editBasicPremiumState,
  EDIT_PENSION_AGE_BUMNAN_SMART,
  EDIT_PENSION_AGE_KOOMVER_RETIRE,
} from 'quick-quote/product-common/coverage-plan/actions'
import { getSumAssured } from 'quick-quote/product-common/coverage-plan/selectors'
import { getDisplayProducts, getSelectedDisplayProductCode } from 'quick-quote/product-selection/selectors'

import { SELECT_PRODUCT_BUMNAN_READY, SELECT_PRODUCT_BUMNAN_SMART, selectBasicPlan } from './actions'
import {
  getAvaliableProductBumnanSmart,
  getAvaliableProductKoomverRetire,
  getPensionAge,
  getSelectableProductBumnanReady,
  getSelectedBumnanSmartPlanGroup,
  getSelectedProductBumnanReady,
} from './coverage-plan/selectors'

/**
 * createProductBumnanReadyFunc
 * @param productCommonCoverageFunc {ReturnType<import('core/service-wrapper/legacy/handler/product-common-coverage').createProductCommonCoverageFunc>}
 */
export function createProductBumnanReadyFunc(productCommonCoverageFunc) {
  const onUpdateSelectedBumnanReady = function*(action) {
    const displayProducts = yield select(getDisplayProducts)
    const productSelected = _.find(displayProducts, { code: action.payload })
    if (productSelected) {
      const sumAssured = yield select(getSumAssured)
      yield put(selectBasicPlan(productSelected))
      yield put(editSumAssured(sumAssured))
      yield put(editBasicPremiumState(-1))
    }
  }

  const switchBumnanReadyProductByAge = function*(action) {
    const productCode = yield select(getSelectedDisplayProductCode)
    const selectableProducts = yield select(getSelectableProductBumnanReady)
    const selectedProductBumnanReady = getSelectedProductBumnanReady(selectableProducts, productCode)
    if (
      !!selectedProductBumnanReady &&
      selectedProductBumnanReady !== productCode &&
      CONSTANTS.BUMNAN_READY_CODE_GROUP.includes(productCode)
    ) {
      const displayProducts = yield select(getDisplayProducts)
      const productSelected = _.find(displayProducts, { code: selectedProductBumnanReady })
      yield put(selectBasicPlan(productSelected))
      yield call(productCommonCoverageFunc.populateAvailableRiders)
    }
  }

  const switchBumnanSmartProductByPensionAge = function*(action) {
    const productCode = yield select(getSelectedDisplayProductCode)
    if (!CONSTANTS.BUMNAN_SMART_GROUP.includes(productCode)) return

    const selectedBumnanSmartPlanGroup = yield select(getSelectedBumnanSmartPlanGroup)
    const avaliableProductBumnanSmart = yield select(getAvaliableProductBumnanSmart)
    const filterSelectedBumnanSmartPlanGroup = _.filter(avaliableProductBumnanSmart, (product) =>
      _.includes(selectedBumnanSmartPlanGroup, _.get(product, 'code'))
    )
    const bumnanSmartPayload = _.find(filterSelectedBumnanSmartPlanGroup, { pensionAge: action.payload })
    if (bumnanSmartPayload && bumnanSmartPayload.code !== productCode) {
      const sumAssured = yield select(getSumAssured)
      yield put(selectBasicPlan(bumnanSmartPayload))
      yield put(editSumAssured(sumAssured))
      yield put(editBasicPremiumState(-1))
    }
  }

  const switchKoomverRetireProductByPensionAge = function*(action) {
    const productCode = yield select(getSelectedDisplayProductCode)
    if (!CONSTANTS.KOOMVER_RETIRE_GROUP.includes(productCode)) return

    const avaliableProductKoomverRetire = yield select(getAvaliableProductKoomverRetire)
    const koomverRetirePayload = _.find(avaliableProductKoomverRetire, { pensionAge: action.payload })
    if (koomverRetirePayload && koomverRetirePayload.code !== productCode) {
      const sumAssured = yield select(getSumAssured)
      yield put(selectBasicPlan(koomverRetirePayload))
      yield put(editSumAssured(sumAssured))
      yield put(editBasicPremiumState(-1))
    }
  }

  const switchBumnanSmartProduct = function*(action) {
    if (!CONSTANTS.BUMNAN_SMART_GROUP.includes(action.payload)) return

    const avaliableProductBumnanSmart = yield select(getAvaliableProductBumnanSmart)
    const productCodeGroupSelected = CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_6.includes(action.payload)
      ? CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_6
      : CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_AGE
    const filterSelectedBumnanSmartPlanGroup = _.filter(avaliableProductBumnanSmart, (product) =>
      _.includes(productCodeGroupSelected, _.get(product, 'code'))
    )

    const pensionAge = yield select(getPensionAge)
    const bumnanSmartPayload = _.find(filterSelectedBumnanSmartPlanGroup, { pensionAge: pensionAge })
    if (bumnanSmartPayload) {
      const sumAssured = yield select(getSumAssured)
      yield put(selectBasicPlan(bumnanSmartPayload))
      yield put(editSumAssured(sumAssured))
      yield put(editBasicPremiumState(-1))
    }
  }

  const switchBumnanSmartProductByAge = function*(action) {
    const productCode = yield select(getSelectedDisplayProductCode)
    const avaliableProductBumnanSmart = yield select(getAvaliableProductBumnanSmart)
    const listAvaliableProductCodeBumnanSmart = avaliableProductBumnanSmart.map((product) => product.code)
    const hasProductActiveInGroup = listAvaliableProductCodeBumnanSmart.includes(productCode)

    if (!hasProductActiveInGroup && listAvaliableProductCodeBumnanSmart.length > 0) {
      const filterSelectedBumnanSmartPlan_6 = _.filter(avaliableProductBumnanSmart, (product) =>
        _.includes(CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_6, _.get(product, 'code'))
      )
      const filterSelectedBumnanSmartPlan_AGE = _.filter(avaliableProductBumnanSmart, (product) =>
        _.includes(CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_AGE, _.get(product, 'code'))
      )

      const hasArrayDataBumnanSmart_6 = (filterSelectedBumnanSmartPlan_6) =>
        Array.isArray(filterSelectedBumnanSmartPlan_6) && filterSelectedBumnanSmartPlan_6.length > 0
      const hasArrayDataBumnanSmart_AGE = (filterSelectedBumnanSmartPlan_AGE) =>
        Array.isArray(filterSelectedBumnanSmartPlan_AGE) && filterSelectedBumnanSmartPlan_AGE.length > 0

      let payloadProductSelected
      if (CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_6.includes(productCode)) {
        if (hasArrayDataBumnanSmart_6(filterSelectedBumnanSmartPlan_6)) {
          payloadProductSelected = filterSelectedBumnanSmartPlan_6[0]
        } else if (hasArrayDataBumnanSmart_AGE(filterSelectedBumnanSmartPlan_AGE)) {
          payloadProductSelected = filterSelectedBumnanSmartPlan_AGE[0]
        }
      } else if (CONSTANTS.BUMNAN_SMART_GROUP_CODE.BUMNAN_SMART_AGE.includes(productCode)) {
        if (hasArrayDataBumnanSmart_AGE(filterSelectedBumnanSmartPlan_AGE)) {
          payloadProductSelected = filterSelectedBumnanSmartPlan_AGE[0]
        } else if (hasArrayDataBumnanSmart_6(filterSelectedBumnanSmartPlan_6)) {
          payloadProductSelected = filterSelectedBumnanSmartPlan_6[0]
        }
      }

      yield put(selectBasicPlan(payloadProductSelected))
      yield call(productCommonCoverageFunc.populateAvailableRiders)
    }
  }

  const switchKoomverRetireProductByAge = function*(action) {
    const productCode = yield select(getSelectedDisplayProductCode)
    const avaliableProductKoomverRetire = yield select(getAvaliableProductKoomverRetire)
    const listAvaliableProductCodeKoomverRetire = avaliableProductKoomverRetire.map((product) => product.code)
    const hasProductActiveInGroup = listAvaliableProductCodeKoomverRetire.includes(productCode)

    if (!hasProductActiveInGroup && listAvaliableProductCodeKoomverRetire.length > 0) {
      yield put(selectBasicPlan(avaliableProductKoomverRetire[0]))
      yield call(productCommonCoverageFunc.populateAvailableRiders)
    }
  }

  const handleUpdateBirthDateWithProductBumnan = function*(action) {
    const productCode = yield select(getSelectedDisplayProductCode)
    if (CONSTANTS.BUMNAN_READY_CODE_GROUP.includes(productCode)) {
      yield call(switchBumnanReadyProductByAge, action)
    } else if (CONSTANTS.BUMNAN_SMART_GROUP.includes(productCode)) {
      yield call(switchBumnanSmartProductByAge, action)
    } else if (CONSTANTS.KOOMVER_RETIRE_GROUP.includes(productCode)) {
      yield call(switchKoomverRetireProductByAge, action)
    } else {
      return
    }
  }

  return {
    onUpdateSelectedBumnanReady,
    switchBumnanReadyProductByAge,
    switchBumnanSmartProductByPensionAge,
    switchKoomverRetireProductByPensionAge,
    switchBumnanSmartProduct,
    switchBumnanSmartProductByAge,
    switchKoomverRetireProductByAge,
    handleUpdateBirthDateWithProductBumnan,
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
export function* watchers(serviceWrapper) {
  const { BUMNAN_READY } = serviceWrapper.products
  yield [
    takeLatest([UPDATE_BIRTHDATE], BUMNAN_READY.switchBumnanReadyProductByAge),
    takeLatest([SELECT_PRODUCT_BUMNAN_READY], BUMNAN_READY.onSelectProduct),
    takeLatest([EDIT_PENSION_AGE_BUMNAN_SMART], BUMNAN_READY.switchBumnanSmartProductByPensionAge),
    takeLatest([EDIT_PENSION_AGE_KOOMVER_RETIRE], BUMNAN_READY.switchKoomverRetireProductByPensionAge),
    takeLatest([SELECT_PRODUCT_BUMNAN_SMART], BUMNAN_READY.switchBumnanSmartProduct),
  ]
}

export const sagas = watchers
